import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listOrganizationsForSelector, getSites } from '../../services/asset.service';
import { resolveOrgIdentity } from '../orgIdentity';
import {
  scopeSelectorOrganizationsQueryKey,
  scopeSelectorSitesQueryKey,
} from '../../queryCacheScope';

function readStoredScopeId(key) {
  try {
    return String(localStorage.getItem(key) || '').trim();
  } catch {
    return '';
  }
}

function writeStoredScopeId(key, value) {
  try {
    const next = String(value || '').trim();
    if (next) localStorage.setItem(key, next);
    else localStorage.removeItem(key);
  } catch {
    /* ignore quota / private mode */
  }
}

export function useScopeSelector() {
  // Hydrate from localStorage synchronously so route remounts (Assets → Scans)
  // do not flash "All Organizations" while orgs reload.
  const [selectedOrgId, setSelectedOrgId] = useState(() => readStoredScopeId('xspectre_selected_org_id'));
  const [selectedSiteId, setSelectedSiteId] = useState(() => readStoredScopeId('xspectre_selected_site_id'));
  const [cachedOrgName, setCachedOrgName] = useState(() => readStoredScopeId('xspectre_selected_org_name'));

  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');

  const dashboardScopeQuery = useMemo(() => {
    const org = String(selectedOrgId || '').trim();
    const site = String(selectedSiteId || '').trim();
    if (!org && !site) return null;
    const q = {};
    if (org) q.org_id = org;
    if (site) q.site_uuid = site;
    return q;
  }, [selectedOrgId, selectedSiteId]);

  const earlyIntelScopeQuery = useMemo(() => {
    const org = String(selectedOrgId || '').trim();
    const site = String(selectedSiteId || '').trim();
    if (!org && !site) return null;
    const q = {};
    if (org) q.org_id = org;
    if (site) q.site_uuid = site;
    return q;
  }, [selectedOrgId, selectedSiteId]);

  const getOrgIdentity = useCallback((org) => resolveOrgIdentity(org), []);

  const findOrgByAnyId = useCallback((list, rawId) => {
    const needle = String(rawId || '').trim().toLowerCase();
    if (!needle || !Array.isArray(list)) return null;
    return list.find((o) => {
      const id = String(o?.id ?? '').trim().toLowerCase();
      const uuid = String(o?.uu_id ?? o?.org_uuid ?? o?.organization_uuid ?? '').trim().toLowerCase();
      const identity = getOrgIdentity(o).toLowerCase();
      return identity === needle || id === needle || uuid === needle;
    }) || null;
  }, [getOrgIdentity]);

  const organizationsQuery = useQuery({
    queryKey: scopeSelectorOrganizationsQueryKey(),
    queryFn: async () => {
      const result = await listOrganizationsForSelector();
      const list = Array.isArray(result) ? result : (result?.data ?? []);
      return Array.isArray(list) ? list : [];
    },
  });
  const orgs = useMemo(() => organizationsQuery.data || [], [organizationsQuery.data]);
  // A background refresh must not flip the header (and every page behind it) back to loading.
  const orgsLoading = organizationsQuery.isPending && !organizationsQuery.data;
  const orgsError = organizationsQuery.error?.message || '';
  const orgsHint = orgs.length <= 1 && !orgsLoading
    ? 'Only organizations assigned to your account are shown here.'
    : '';

  useEffect(() => {
    if (orgs.length === 0) return;
    const saved = readStoredScopeId('xspectre_selected_org_id');
    setSelectedOrgId((current) => {
      const match = findOrgByAnyId(orgs, current) || findOrgByAnyId(orgs, saved);
      if (!match) return String(current || '').trim() ? String(current).trim() : '';
      const normalized = getOrgIdentity(match);
      const name = String(match?.name || match?.org_name || '').trim();
      if (normalized && normalized !== saved) writeStoredScopeId('xspectre_selected_org_id', normalized);
      if (name) {
        writeStoredScopeId('xspectre_selected_org_name', name);
        setCachedOrgName(name);
      }
      return normalized;
    });
  }, [findOrgByAnyId, getOrgIdentity, orgs]);

  const headerSelectedOrgName = useMemo(() => {
    const id = String(selectedOrgId || '').trim();
    if (!id) return '';
    const match = findOrgByAnyId(orgs, id);
    const fromList = String(match?.name || match?.org_name || '').trim();
    return fromList || cachedOrgName || '';
  }, [selectedOrgId, orgs, findOrgByAnyId, cachedOrgName]);

  const filteredHeaderOrgs = useMemo(() => {
    const q = String(orgSearch || '').trim().toLowerCase();
    const list = !q
      ? orgs
      : orgs.filter((o) => String(o?.name || '').toLowerCase().includes(q));
    return [...list].sort((a, b) => {
      const an = String(a?.name || a?.org_name || '').trim();
      const bn = String(b?.name || b?.org_name || '').trim();
      return an.localeCompare(bn, undefined, { sensitivity: 'base' });
    });
  }, [orgSearch, orgs]);

  const normalizeHeaderSiteRow = useCallback((site, idx) => {
    const id = String(site?.uu_id || site?.site_uuid || site?.uuid || site?.id || '').trim();
    if (!id) return null;
    const name = String(site?.site_name || site?.name || site?.site || '').trim() || `Site ${idx + 1}`;
    const nestedOrg = typeof site?.organization === 'object' && site.organization
      ? site.organization
      : null;
    const orgUuid = String(
      site?.org_uuid ||
      site?.organization_uuid ||
      site?.organization_uu_id ||
      nestedOrg?.uu_id ||
      nestedOrg?.org_uuid ||
      ''
    ).trim();
    const orgNumericId = String(
      site?.organization_id || site?.org_id || nestedOrg?.id || ''
    ).trim();
    const orgName = String(
      (typeof site?.organization === 'string' ? site.organization : '') ||
      nestedOrg?.name ||
      nestedOrg?.org_name ||
      site?.organization_name ||
      ''
    ).trim().toLowerCase();
    return {
      id,
      name,
      orgId: orgUuid || orgNumericId,
      orgUuid,
      orgNumericId,
      orgName,
      description: String(site?.site_disc || site?.description || '').trim(),
      details: String(site?.site_owner || site?.site_details || '').trim(),
      status: String(site?.site_status || '').trim(),
      isActive: typeof site?.is_active === 'boolean' ? site.is_active : true,
    };
  }, []);

  const sitesQuery = useQuery({
    queryKey: scopeSelectorSitesQueryKey(selectedOrgId),
    queryFn: async () => {
      const org = String(selectedOrgId || '').trim();
      // Backend Site.get filters on org_uu_id (UUID), not org_id.
      const raw = await getSites(null, org ? { org_uu_id: org } : null);
      return (Array.isArray(raw) ? raw : []).map(normalizeHeaderSiteRow).filter(Boolean);
    },
  });
  const { refetch: refetchSites } = sitesQuery;
  const headerSites = useMemo(() => {
    const rows = sitesQuery.data || [];
    const selected = String(selectedOrgId || '').trim().toLowerCase();
    if (!selected) return rows;
    const selectedOrg = findOrgByAnyId(orgs, selectedOrgId);
    const selectedOrgName = String(
      selectedOrg?.name || selectedOrg?.org_name || cachedOrgName || ''
    ).trim().toLowerCase();
    const allowed = new Set(
      [
        selected,
        getOrgIdentity(selectedOrg).toLowerCase(),
        String(selectedOrg?.id ?? '').trim().toLowerCase(),
        String(selectedOrg?.uu_id ?? selectedOrg?.org_uuid ?? '').trim().toLowerCase(),
      ].filter(Boolean)
    );
    // Client-side safety net if API ignored the org filter or identity forms differ.
    return rows.filter((site) => {
      const siteOrgKeys = [
        String(site?.orgId || '').trim().toLowerCase(),
        String(site?.orgUuid || '').trim().toLowerCase(),
        String(site?.orgNumericId || '').trim().toLowerCase(),
      ].filter(Boolean);
      if (siteOrgKeys.some((key) => allowed.has(key))) return true;
      // Last resort: match org display name when UUID fields are missing.
      if (selectedOrgName && site?.orgName && site.orgName === selectedOrgName) return true;
      // Do not keep unscoped rows while an org is selected — that was leaking "all sites".
      return false;
    });
  }, [sitesQuery.data, selectedOrgId, orgs, findOrgByAnyId, getOrgIdentity, cachedOrgName]);
  const headerSitesLoading = sitesQuery.isPending && !sitesQuery.data;
  // A persisted scope may be corrected after organizations/sites arrive. Consumers
  // should wait only when there is a persisted selection to validate; an unscoped
  // dashboard can load immediately.
  const scopeReady =
    (!selectedOrgId || !orgsLoading) &&
    (!selectedSiteId || !headerSitesLoading);

  useEffect(() => {
    if (!sitesQuery.isSuccess) return;
    const saved = readStoredScopeId('xspectre_selected_site_id');
    if (saved && headerSites.some((site) => site.id === saved)) {
      setSelectedSiteId(saved);
      return;
    }
    // When an org is picked and it only has one site, select that site immediately.
    if (selectedOrgId && headerSites.length === 1) {
      const onlySiteId = String(headerSites[0]?.id || '').trim();
      if (onlySiteId) {
        setSelectedSiteId(onlySiteId);
        writeStoredScopeId('xspectre_selected_site_id', onlySiteId);
        return;
      }
    }
    setSelectedSiteId('');
    if (saved) writeStoredScopeId('xspectre_selected_site_id', '');
  }, [headerSites, selectedOrgId, sitesQuery.isSuccess]);

  const filteredHeaderSites = useMemo(() => {
    const q = String(siteSearch || '').trim().toLowerCase();
    if (!q) return headerSites;
    return headerSites.filter((s) => String(s?.name || '').toLowerCase().includes(q));
  }, [siteSearch, headerSites]);

  const loadHeaderSites = useCallback(() => refetchSites(), [refetchSites]);

  const handleOrgSelect = useCallback((org) => {
    const val = getOrgIdentity(org);
    if (!val) return;
    const name = String(org?.name || org?.org_name || '').trim();
    setSelectedOrgId(val);
    writeStoredScopeId('xspectre_selected_org_id', val);
    setCachedOrgName(name);
    writeStoredScopeId('xspectre_selected_org_name', name);
    setSelectedSiteId('');
    writeStoredScopeId('xspectre_selected_site_id', '');
    setOrgDropdownOpen(false);
    setOrgSearch('');
  }, [getOrgIdentity]);

  const handleClearOrgSelection = useCallback(() => {
    setSelectedOrgId('');
    setCachedOrgName('');
    writeStoredScopeId('xspectre_selected_org_id', '');
    writeStoredScopeId('xspectre_selected_org_name', '');
    setSelectedSiteId('');
    writeStoredScopeId('xspectre_selected_site_id', '');
    setOrgDropdownOpen(false);
    setOrgSearch('');
  }, []);

  const handleSiteSelect = useCallback((row) => {
    if (!row) {
      setSelectedSiteId('');
      writeStoredScopeId('xspectre_selected_site_id', '');
    } else {
      const val = String(row.id || '').trim();
      if (!val) return;

      // Selecting a site always scopes to its organization as well.
      const orgNeedle = String(row.orgUuid || row.orgId || row.orgNumericId || '').trim();
      const matchedOrg =
        findOrgByAnyId(orgs, orgNeedle) ||
        (row.orgName
          ? orgs.find((org) => {
              const name = String(org?.name || org?.org_name || '').trim().toLowerCase();
              return Boolean(name) && name === String(row.orgName).trim().toLowerCase();
            })
          : null) ||
        null;

      if (matchedOrg) {
        const orgId = getOrgIdentity(matchedOrg);
        const orgName = String(matchedOrg?.name || matchedOrg?.org_name || '').trim();
        if (orgId) {
          setSelectedOrgId(orgId);
          writeStoredScopeId('xspectre_selected_org_id', orgId);
        }
        if (orgName) {
          setCachedOrgName(orgName);
          writeStoredScopeId('xspectre_selected_org_name', orgName);
        }
      } else if (orgNeedle) {
        setSelectedOrgId(orgNeedle);
        writeStoredScopeId('xspectre_selected_org_id', orgNeedle);
      }

      setSelectedSiteId(val);
      writeStoredScopeId('xspectre_selected_site_id', val);
    }
    setSiteDropdownOpen(false);
    setSiteSearch('');
  }, [findOrgByAnyId, getOrgIdentity, orgs]);

  return {
    orgs,
    selectedOrgId,
    setSelectedOrgId,
    selectedSiteId,
    setSelectedSiteId,
    dashboardScopeQuery,
    earlyIntelScopeQuery,
    scopeReady,
    orgDropdownOpen,
    setOrgDropdownOpen,
    orgSearch,
    setOrgSearch,
    orgsLoading,
    orgsError,
    orgsHint,
    siteDropdownOpen,
    setSiteDropdownOpen,
    siteSearch,
    setSiteSearch,
    headerSites,
    headerSitesLoading,
    getOrgIdentity,
    headerSelectedOrgName,
    filteredHeaderOrgs,
    filteredHeaderSites,
    handleOrgSelect,
    handleClearOrgSelection,
    handleSiteSelect,
    loadHeaderSites,
  };
}
